import { useCallback, useMemo } from 'react';
import { HealthStatus, SchedulingStage, AddNotePayload } from './SchedulingContextTypes';

export const useSLAHealth = (promisedDate: string, currentStage: SchedulingStage, currentHealth: HealthStatus) => {
  const daysRemaining = useMemo(() => {
    const promised = new Date(promisedDate);
    const now = new Date();
    return Math.ceil((promised.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }, [promisedDate]);

  const isAtRisk = useMemo(() => {
    const criticalStages: SchedulingStage[] = [
      'Design',
      'Manufacturing',
      'Testing',
      'Loading'
    ];
    const isCritical = criticalStages.includes(currentStage);
    return isCritical ? daysRemaining < 7 : daysRemaining < 3;
  }, [daysRemaining, currentStage]);

  // Compute recommended health based on SLA
  const getRecommendedHealth = useCallback(
    (note?: AddNotePayload): HealthStatus => {
      if (!note) return currentHealth;

      // If note indicates delay expected
      if (note.impactLevel === 'delay-expected') {
        return 'delayed';
      }

      // If note is a risk/issue
      if (note.noteType === 'risk') {
        return 'at-risk';
      }

      // If SLA is at risk and no mitigating note, escalate
      if (isAtRisk && note.noteType !== 'milestone') {
        return 'at-risk';
      }

      return currentHealth;
    },
    [currentHealth, isAtRisk]
  );

  return { getRecommendedHealth };
};
